/** @jsxImportSource @emotion/react */

import { useState, useEffect, FC } from "react";
import { css } from "@emotion/core";
import { useHistory, useParams } from "react-router-dom";
import GridCell from "ui-modules/components/Layout/Grid/GridCell";
import Grid from "ui-modules/components/Layout/Grid";
import Skeleton from "ui-modules/components/Misc/Skeleton";
import rest from "../../../utils/rest";
import { enNumberToPersianNumber } from "../../../utils/commons";
import Paper from "../../../components/Paper";
import Img from "ui-modules/components/Image/Img";
import zitelLogo from "../../../assets/images/zitel-logo.svg";
import Captcha from "ui-modules/components/Misc/Captcha";
import Button from "ui-modules/components/Form/Button";
import Text from "ui-modules/components/DataDisplay/Text";
import useNotification from "ui-modules/commons/hooks/use-notification";
import useTheme from "ui-modules/commons/hooks/use-theme";
import RenewInfoBanner from "../../../components/RenewInfoBanner";

type TimeIntervalType = {
  key?: string;
  timeAmount: string | number;
  discountPercent?: number;
};

type AgreementItemType = {
  agreementItemId?: string;
  serviceTitle?: string;
  serviceNumber?: string;
  customerNumber?: string;
  address?: string;
  timeIntervals?: TimeIntervalType[];
};

const getIntervalKey = (interval: TimeIntervalType) => interval.key ?? `${interval.timeAmount}-MONTH`;

const getIntervalLabel = (timeAmount: string | number) => `تمدید ${enNumberToPersianNumber(timeAmount)} ماهه سرویس`;

const getDiscountLabel = (discountPercent?: number) =>
  discountPercent && discountPercent > 0 ? `شامل ${discountPercent}٪ تخفیف` : undefined;

type RenewByLinkProps = {
  source?: "link" | "zitel";
};

const RenewByLink: FC<RenewByLinkProps> = ({ source = "link" }) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const notification = useNotification();
  const theme = useTheme();

  const [agreementItem, setAgreementItem] = useState<AgreementItemType | null>(null);
  const [isLoading, setIsLoading] = useState(source === "zitel" || !!id);
  const [isError, setIsError] = useState(false);
  const [selectedTimeInterval, setSelectedTimeInterval] = useState<string | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState<{ captchaImage: string; captchaId: string; loading: boolean }>({
    captchaImage: "",
    captchaId: "",
    loading: false,
  });

  const getCaptcha = () => {
    setCaptcha((prev) => ({ ...prev, loading: true }));
    rest
      .post(`${process.env.REACT_APP_AUTH_SERVICE}/api/captcha`)
      .then((resp) => {
        const image = resp?.data?.image || "";
        setCaptcha({
          captchaImage: image.length ? `data:image/gif;base64,${image}` : "",
          captchaId: resp?.data?.id || "",
          loading: false,
        });
      })
      .catch(() => {
        setCaptcha({ captchaImage: "", captchaId: "", loading: false });
      });
  };

  useEffect(() => {
    getCaptcha();
  }, []);

  useEffect(() => {
    if (source === "link" && !id) return;

    const request =
      source === "zitel"
        ? rest.get(`${process.env.REACT_APP_BSS_SERVICE}/api/web-portal/load-zitel-service`, {
            headers: { "X-Auth": "no-auth" },
          })
        : rest.get(`${process.env.REACT_APP_BSS_SERVICE}/api/web-portal/load-service?uuid-code=${id}`);

    request
      .then((resp) => {
        if (source === "zitel") {
          console.log("load-zitel-service response:", resp?.data);
        }
        const service = resp?.data ?? null;
        if (!service) {
          setIsError(true);
          return;
        }
        setAgreementItem(service);
      })
      .catch((err) => {
        if (source === "zitel") {
          console.log("load-zitel-service error:", err?.response?.data ?? err);
        }
        setIsError(true);
        notification.error({ message: "خطا، لطفا مجددا تلاش نمایید." });
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, source]);

  const handleError = (err: any) => {
    let message = "";
    try {
      message = JSON.parse(err?.response?.data?.message || "")?.fa || "";
    } catch (_) {}
    notification.error({
      message: message || err?.response?.data?.message || "خطا در ایجاد پیش‌فاکتور، لطفا مجددا تلاش کنید.",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);

    const body = {
      username: agreementItem?.customerNumber,
      captchaId: captcha.captchaId,
      captchaAnswer,
    };

    rest
      .post(
        `${process.env.REACT_APP_BSS_SERVICE}/api/web-portal/agreement_item/${agreementItem?.agreementItemId}/proforma?time-interval=${selectedTimeInterval}`,
        body
      )
      .then((resp) => {
        const selectedInterval = agreementItem?.timeIntervals?.find(
          (interval) => getIntervalKey(interval) === selectedTimeInterval
        );

        history.replace("/renew-proforma", {
          proformaData: resp?.data,
          agreementItemId: agreementItem?.agreementItemId,
          selectedPeriodLabel: selectedInterval ? getIntervalLabel(selectedInterval.timeAmount) : undefined,
          timeInterval: selectedTimeInterval ?? undefined,
        });
      })
      .catch((err) => {
        setCaptchaAnswer("");
        getCaptcha();
        setSubmitting(false);
        handleError(err);
      });
  };

  const isValid = selectedTimeInterval !== null && captchaAnswer.length > 0;

  const style = css`
    .inovice-wrapper {
      height: 95vh;
      text-align: center;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    Img {
      width: 100px;
      margin-top: 20px;
    }
    .top-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0 auto 10px;
      width: 100%;
      max-width: 800px;
    }
    .title {
      margin-bottom: 30px;
    }
    .back-btn {
      cursor: pointer;
      font-size: 13px;
      color: ${theme.material.textHintOnBackground};
      &:hover {
        color: #fff;
      }
    }
    .service-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      margin-bottom: 12px;
      width: 100%;
    }
    .service-section {
      margin-bottom: 10px;
      border-radius: 10px;
      padding: 20px;
      text-align: right;
      direction: rtl;
      width: 100%;
      max-width: 800px;
    }
    .service-name {
      font-weight: bold;
      font-size: 17px;
      color: ${theme.material.textPrimaryOnBackground};
      margin-bottom: 10px;
      display: block;
    }
    .service-id-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      margin-bottom: 16px;
    }
    .service-id-label {
      color: #aaa;
    }
    .service-id-value {
      color: #ccc;
      word-break: break-word;
    }
    .divider {
      border: none;
      border-top: 1px solid #3a3a3a;
      margin: 0 0 16px;
    }
    .renew-option {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      direction: ltr;
      gap: 10px;
      padding: 10px 0;
      cursor: pointer;
      border-bottom: 1px solid #3a3a3a;
      &:last-child {
        border-bottom: none;
      }
    }
    .renew-label {
      font-size: 14px;
      color: ${theme.material.textPrimaryOnBackground};
    }
    .discount-badge {
      font-size: 11px;
      color: #f0a500;
      background: rgba(240, 165, 0, 0.12);
      padding: 2px 8px;
      border-radius: 20px;
    }
    .radio-circle {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid ${theme.material.textHintOnBackground};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .radio-circle.selected {
      border-color: #00b09b;
    }
    .radio-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: #00b09b;
    }
    .form-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .content-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
    .handle-error {
      padding-top: 50px;
      display: inline-block;
    }
  `;

  return (
    <Grid css={style}>
      <GridCell phone={4} tablet={8} desktop={12}>
        <div className="inovice-wrapper">
          <Img src={zitelLogo} alt="zitel-logo" />
          {isLoading ? (
            <div className="content-wrapper">
              <Skeleton width="100%" height={200} />
            </div>
          ) : (source === "link" && !id) || isError ? (
            <div className="handle-error">
              <Text>خطایی در نمایش اطلاعات سرویس رخ داده است</Text>
            </div>
          ) : (
            <div className="content-wrapper">
              <div className="top-bar">
                <Text>مشترک گرامی جهت تمدید سرویس، دوره مورد نظرتان را انتخاب کنید.</Text>
              </div>
              <div className="service-wrapper">
                <Paper className="service-section">
                  <span className="service-name">{agreementItem?.serviceTitle ?? "—"}</span>
                  <div className="service-id-row">
                    <span className="service-id-label">شناسه توافق‌نامه:</span>
                    <span className="service-id-value">{agreementItem?.serviceNumber ?? "—"}</span>
                  </div>
                  <div className="service-id-row">
                    <span className="service-id-label">آدرس:</span>
                    <span className="service-id-value">{agreementItem?.address ?? "—"}</span>
                  </div>
                  <hr className="divider" />
                  {(agreementItem?.timeIntervals ?? []).map((interval) => {
                    const intervalKey = getIntervalKey(interval);
                    const discountLabel = getDiscountLabel(interval.discountPercent);

                    return (
                      <div
                        key={intervalKey}
                        className="renew-option"
                        onClick={() => setSelectedTimeInterval(intervalKey)}
                      >
                        {discountLabel && <span className="discount-badge">({discountLabel})</span>}
                        <span className="renew-label">{getIntervalLabel(interval.timeAmount)}</span>
                        <div className={`radio-circle${selectedTimeInterval === intervalKey ? " selected" : ""}`}>
                          {selectedTimeInterval === intervalKey && <div className="radio-dot" />}
                        </div>
                      </div>
                    );
                  })}
                </Paper>
              </div>
              <div style={{ width: "100%", maxWidth: 800, margin: "12px 0" }}>
                <RenewInfoBanner message="در صورت تمدید پیش از انقضا، تعداد روزهای باقی مانده از سرویس فعلی به سرویس جدید اضافه خواهد شد." />
              </div>
              <div className="form-wrapper">
                <form onSubmit={handleSubmit}>
                  {captcha.loading ? (
                    <Skeleton width="100%" height={60} />
                  ) : (
                    <Captcha
                      img={captcha.captchaImage}
                      onCaptchaAnswer={setCaptchaAnswer}
                      onReload={getCaptcha}
                      name="captchaAnswer"
                    />
                  )}
                  <div style={{ marginTop: 10 }}>
                    <Button data-cy={"payment-submit"} raised fullSize disabled={!isValid || submitting} type="submit">
                      تایید و پرداخت
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </GridCell>
    </Grid>
  );
};

export default RenewByLink;
